import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import LoadingTable from "../../components/LoadingTable";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";

const MySwal = withReactContent(Swal);

const EducationMasterList = ({
  title,
  addPath,
  editPath,
  listEndpoint,
  deleteEndpoint,
  responseKey,
  includeCategory = false,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${listEndpoint}?page=${currentPage}&limit=${limit}&search=${searchTerm}`
      );
      setData(response.data?.[responseKey] || []);
      setCurrentPage(response.data?.currentPage || 1);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, limit]);

  const handleDelete = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this data!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axiosInstance
          .delete(`${deleteEndpoint}/${id}`)
          .then(() => {
            fetchData();
            toast.success(`${title} deleted successfully!`);
          })
          .catch(() => toast.error("Error deleting data"));
      }
    });
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between w-100 flex-wrap">
            <div className="mb-3 mb-lg-0">
              <h1 className="h4">All {title}</h1>
            </div>
            <Link to={addPath} className="btn btn-primary">
              Add {title}
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="table-responsive py-4">
            {loading ? (
              <LoadingTable />
            ) : (
              <>
                <div className="datatable-top">
                  <div className="datatable-dropdown">
                    <label>
                      <select
                        className="datatable-selector"
                        value={limit}
                        onChange={(event) => {
                          setLimit(parseInt(event.target.value, 10));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={25}>25</option>
                      </select>{" "}
                      entries per page
                    </label>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="datatable-input"
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <button className="btn btn-primary" onClick={fetchData}>
                      Search
                    </button>
                  </div>
                </div>

                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      {includeCategory && <th>Education Category</th>}
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row._id}>
                        <td>{row._id}</td>
                        <td>{row.name}</td>
                        {includeCategory && <td>{row.educationCategory}</td>}
                        <td>
                          <Link
                            to={`${editPath}/${row._id}`}
                            className="btn btn-primary me-2"
                          >
                            Edit
                          </Link>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDelete(row._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="datatable-bottom">
                  <nav className="datatable-pagination">
                    <ul className="datatable-pagination-list">
                      <li className="datatable-pagination-list-item">
                        <button
                          className="datatable-pagination-list-item-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={loading || currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      <li>
                        <div className="datatable-info py-1 px-3">
                          <strong>
                            Page {currentPage} of {totalPages}
                          </strong>
                        </div>
                      </li>
                      <li className="datatable-pagination-list-item">
                        <button
                          className="datatable-pagination-list-item-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={loading || currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default EducationMasterList;
